import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, withMenuLoading } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_SAL_ISU_REG_001_POP_001 from "@menu/SAL/SN_SAL_ISU_REG_001/SN_SAL_ISU_REG_001_POP_001";
import SnButton from "@shared/components/SnButton";
import { fnCheckAutoProcessV2 } from "@menu/PUR/common/sn-pur-util";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-enterprise";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";

interface Props {
  Menuprops: menuInfo;
}

// 출하의뢰등록
const SN_SAL_ISU_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const ioTypeRef = useRef<any[]>([]);
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");
  const isProcessing = useRef(false);

  const [addInfoFlags2, setAddInfoFlags2] = useState<string[]>([]);

  const loadAddInfoFlags = async (
    idTarget = "grid2",
    setFlags = setAddInfoFlags2
  ) => {
    const result: any = await fnGetDataSet({
      id: "sys_addinfo_pop_01_01",
      params: {
        cd_firm: userInfo?.cd_firm ?? "",
        cd_menu: Menuprops?.cd_menu ?? "",
        id_target: idTarget,
      },
    });
    const rows = result?.[0] || [];
    const flags = extractAddInfoFlags(rows);
    setFlags(flags);
    return flags;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });

        const payload = {
          id: "iotype",
          params: { cd_firm: userInfo.cd_firm },
        };
        const data = await fnGetTableById(payload);
        if (Array.isArray(data) && data.length > 0) {
          ioTypeRef.current = data;
        }

        await loadAddInfoFlags("grid2", setAddInfoFlags2);
      } catch (error) {}
    };
    fetchform();
  }, []);

  const drilldownHandledRef = useRef<any>(null);
  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      // 조회·팝업 등으로 컬럼이 다시 만들어질 때마다 드릴다운이 재실행되지 않게(처리탭으로 되돌아가는 문제) 드릴다운 1건당 1번만.
      if (drilldownHandledRef.current !== Menuprops) {
        drilldownHandledRef.current = Menuprops;
        onActivate(Menuprops.nodeData);
      }
    }
  }, [columns, Menuprops]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onPrint,
  }));

  const onSearch = async (searchParams?: any) => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const param = {
        id: "sn_sal_isu_reg_001_01",
        params: searchParams ?? searchRef.current?.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(param);

      const flags2 =
        addInfoFlags2.length > 0
          ? addInfoFlags2
          : await loadAddInfoFlags("grid2", setAddInfoFlags2);

      setDataSources({
        grid1: data[0],
        grid2: hydrateRowsWithAddInfo(data[1] || [], flags2),
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      return gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {
      return false;
    }
  };

  const onBeforeAddRowH = (): any => {
    try {
      const salIsuReqHChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (salIsuReqHChanges.length > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return false;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_biz_isu: userInfo?.cd_biz,
        cd_user_reg: userInfo?.cd_user,
        fg_trns: searchInfo.fg_trns,
        cd_gp: userInfo?.cd_gp,
        cd_dept: userInfo?.cd_dept,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_req: today,
        st_req: "F",
        yn_autoprocess: "N",
        no_hst: "0",
        cd_cust: searchInfo.cd_cust,
        nm_cust: searchInfo.nm_cust,
        nm_cust_sort: searchInfo.nm_cust,
        cd_cust_dlv: searchInfo.cd_cust,
        no_req: "",
        cd_exch: "000",
        rt_exch: 1,
        fg_money: "1",
        fg_io: "1",
        cd_io: "",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        qt_dummy01: 0,
        qt_dummy02: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRowL = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        dt_dlv: today,
        no_line: maxNoLine + 1,
        // no_line_lc: "0",
        no_line_isu_src: "0",
        no_req: focusedNodeH.data?.no_req,
        st_req: focusedNodeH.data?.st_req,
        cd_so: focusedNodeH.data?.cd_so,
        fg_trns: focusedNodeH.data?.fg_trns,
        fg_vat: focusedNodeH.data?.fg_vat,
        qt: 0,
        qt_isu: 0,
        qt_inv: 0,
        qt_ep: 0,
        qt_cls: 0,
        qt_mgmt: 0,
        um: 0,
        am: 0,
        am_k: 0,
        qt_dummy01: 0,
        qt_dummy02: 0,
        qt_dummy03: 0,
        no_line_src_lot: 0,
        um_k_pack: 0,
        um_supply_pack: 0,
        qt_isu_pack: 0,
        qt_cls_pack: 0,
        qt_dir: 0,
        rt_fat: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    let flag = true;
    gridRefs.current["grid2"].current.forEachFilteredNode((node: any) => {
      if (node.data.qt_isu > 0) {
        flag = false;
      }
    });
    if (!flag) {
      await showDialog("alter", "이미 진행된 건입니다.");
      return;
    }

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    const rowCount = gridRefs.current["grid2"].current?.getDisplayedRowCount();
    if (rowCount === 0) return;

    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return;

    let flag = true;
    gridRefs.current["grid2"].current.forEachFilteredNode((node: any) => {
      if (node.data.qt_isu > 0) {
        // 출고수량
        flag = false;
      }
    });
    if (!flag) {
      await showDialog("alter", "이미 진행된 건입니다.");
      return;
    }
    gridRefs.current["grid2"].current?.deleteSelectedRows();
    if (rowCount === 1) {
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      gridRefs.current["grid1"].current.clearBlock(focusedRowNodeH);
    }
  };

  const onSave = async () => {
    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return false;

    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      // L 필수값 체크
      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const salIsuReqHChanges = gridRefs.current["grid1"].current.getChange();
      const salIsuReqLChanges = gridRefs.current["grid2"].current.getChange();

      const headerInserts = salIsuReqHChanges.insert ?? [];
      const lineInserts = salIsuReqLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        salIsuReqHChanges.modifiedRowcount === 0 &&
        salIsuReqLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      if (headerInserts.length > 0) {
        const focusedRowNodeH =
          gridRefs.current["grid1"].current.focusedRowNode();
        const noSlip = await applySlip({
          cd_column: "no_req",
          cd_module: "SAL",
          no_line: 2,
          cd_prefix: "",
          dt: focusedRowNodeH.data.dt_req,
          girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
          inserts: [salIsuReqHChanges, salIsuReqLChanges],
          cd_menu: Menuprops.cd_menu,
        });
      }

      const flagsForSave2 =
        addInfoFlags2.length > 0
          ? addInfoFlags2
          : await loadAddInfoFlags("grid2", setAddInfoFlags2);
      const isuReqLWithAddInfo = {
        ...salIsuReqLChanges,
        insert: (salIsuReqLChanges.insert ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave2),
        })),
        update: (salIsuReqLChanges.update ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave2),
        })),
      };

      const saveData = {
        isureqh: salIsuReqHChanges,
        isureql: isuReqLWithAddInfo,
      };

      const ok = await fnSave("SN_SAL_ISU_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        gridRefs.current["grid2"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const isResolved = nodeData.no_io_chase !== undefined;
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        fg_search: isResolved ? nodeData.no_io_chase : nodeData.no_req,
        dt_from: isResolved ? nodeData.dt_from : nodeData.dt_dlv,
        dt_to: isResolved ? nodeData.dt_to : nodeData.dt_dlv,
        fg_trns: nodeData.fg_trns ?? "",
        cd_cust: "",
        cd_emp: "",
        cd_gp: nodeData.cd_gp ?? "",
        cd_pjt: "",
        cd_so: "",
        st_so: "",
      };
      setFormData(selectedData);
      await withMenuLoading(Menuprops.cd_menu ?? "SN_SAL_ISU_REG_001", () =>
        onSearch(selectedData)
      );
    } else if ((Menuprops.figure as string) === "fg_drilldown.Registration") {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
      if (
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0
      ) {
        const confirm = await showDialog(
          "confirmCancel",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm === true) {
          const isSaved = await onSave();
          if (isSaved) {
            await withMenuLoading(
              Menuprops.cd_menu ?? "SN_SAL_ISU_REG_001",
              () => setDrilldown()
            );
          }
          return;
        } else if (confirm === false) {
          gridRefs.current["grid1"].current.deleteAllRows();
          gridRefs.current["grid2"].current.deleteAllRows();
          gridRefs.current["grid1"].current.resetUpdate();
          gridRefs.current["grid2"].current.resetUpdate();
          await withMenuLoading(Menuprops.cd_menu ?? "SN_SAL_ISU_REG_001", () =>
            setDrilldown()
          );
          return;
        } else {
          return;
        }
      } else {
        await setDrilldown();
      }
    }
  };

  const setDrilldown = async () => {
    isProcessing.current = true;
    try {
      const drill = {
        cd_firm: userInfo?.cd_firm,
        no_so: Menuprops.nodeData.no_docu,
        no_line: Menuprops.nodeData.no_line,
      };

      const param = {
        id: "sn_sal_isu_reg_001_drilldown_01",
        params: drill,
      };
      const [data] = await Promise.all([
        fnGetDataSet(param),
        gridRefs.current["grid1"].current?.whenColumnsReady(),
        gridRefs.current["grid2"].current?.whenColumnsReady(),
      ]);

      const headerResult = data[0];
      const lineResult = data[1];
      if (headerResult) {
        const addedNode = await onInsert();
        if (addedNode) {
          // gridRefs.current["grid1"].current.selectAndFocusRow(addedNode.rowIndex);
          for (const key in headerResult[0]) {
            if (headerResult[0][key]) {
              addedNode.setDataValue(key, headerResult[0][key]);
            }
          }
        }
      }
      if (lineResult) {
        for (const result of lineResult) {
          const addedNode = await onAddRow();
          if (addedNode) {
            // gridRefs.current["grid2"].current.selectAndFocusRow(
            //   addedNode.rowIndex
            // );
            for (const key in result) {
              if (result[key]) {
                addedNode.setDataValue(key, result[key]);
              }
            }
          }
        }
      }
    } finally {
      isProcessing.current = false;
    }
  };

  const onPrint = async () => {
    gridRefs.current["grid1"].current?.stopEditing();
    gridRefs.current["grid2"].current?.stopEditing();

    const selectedRow = gridRefs.current["grid1"].current?.getSelectedRows();
    return {
      cd_biz: selectedRow[0].cd_biz,
      no_req: selectedRow[0].no_req,
    };
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const salIsuReqHChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (salIsuReqHChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: salIsuReqHChanges[0]?.cd_cust,
        nm_cust_sort: salIsuReqHChanges[0]?.nm_cust_sort,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        fg_trns: salIsuReqHChanges[0]?.fg_trns,
      };

      const component = (
        <SN_SAL_ISU_REG_001_POP_001 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "수주적용",
        children: component,
        validation: true,
        width: "1200px",
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult: any[] = result[1];

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const today = dateToString(new Date());
      if (focusedRowNodeH) {
        for (const key in headerResult[0]) {
          if (key === "no_line") continue;
          focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        }
        focusedRowNodeH.setDataValue(
          "cd_biz_isu",
          headerResult[0].cd_biz,
          "noEvent"
        );
        focusedRowNodeH.setDataValue("dt_req", today, "noEvent");

        let ioType: any;
        for (const io of ioTypeRef.current) {
          if (io.cd_io === headerResult[0].cd_io) ioType = io;
        }
        if (ioType) {
          focusedRowNodeH.setDataValue("fg_money", ioType.fg_money, "noEvent");
        }

        gridRefs.current["grid1"].current.applyBlock(focusedRowNodeH);
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              if (key === "no_line") continue;
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("no_line_so", result.no_line, "noEvent");
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
          }
        }
      });

      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      const dt_req = selectedRow.dt_req || "";
      setSelectedDocu(no_req);
      setSelectedDtDocu(dt_req);
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
    } else {
      return;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_cust") {
      rowNode.setDataValue("cd_cust_dlv", rowData.cd_cust);
      rowNode.setDataValue("nm_cust_dlv", rowData.nm_cust);
    }
    await CalAmBulk(
      gridRefs.current["grid1"],
      gridRefs.current["grid2"],
      colId
    );
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    if (isProcessing.current) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();

    if (colId === "cd_wh") {
      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_wh) return;

      let apply = false;
      gridRefs.current["grid2"].current?.forEachFilteredNode(
        (node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        }
      );
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );
      if (confirm) {
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          }
        );
      }
    }
    await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onOpenAddInfoPopup = async (idTarget = "grid2") => {
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos: columns.grid2 || [],
    });
    if (!saved) return;

    const rtn: any = await getInitFormData();
    setFormControlData(rtn.formControlData);
    setFormData((prev: any) => ({
      ...(rtn.formData || {}),
      ...(prev || {}),
    }));
    setColums({
      grid1: rtn.grid.grid1,
      grid2: rtn.grid.grid2,
    });

    await loadAddInfoFlags(idTarget);
    await onSearch();
  };

  const onGrid2ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    switch (event.id) {
      case "extra_item_setting":
        // 부가항목설정 실행
        await onOpenAddInfoPopup("grid2");
        break;
      default:
        break;
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onBeforeAddRow: onBeforeAddRowH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged,
          onBeforeAddRow: onBeforeAddRowL,
          onBtnDeleteClick: onGrid2BtnsClick,
          onExtraHeaderMenuClick: onGrid2ExtraHeaderMenuClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "출하정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
            header={
              <div className="flex justify-end">
                <SnButton
                  text={"수주적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{
              rowSelection: "multiple",
              title: "상세정보",
              extraHeaderMenuItems: [
                { id: "extra_item_setting", name: "부가항목 설정" },
              ],
            }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
          selectedDtDocu={selectedDtDocu}
        />
      </div>
    </div>
  );
});

export default SN_SAL_ISU_REG_001;
